/* eslint-disable react/prop-types */
import { useState } from "react";
import Swal from "sweetalert2";
import config from "../../../../../../config.json";
import imgVide from "../../../../../assets/images/vide.png";
import "../../../Dashboard/events.css";
import { pickDetail } from "../../../Cms/MenuTree/menuTreeUtils";

export const buildProductShareUrl = (productId) => {
  if (!productId) return "";
  const apiBase = String(config.dev || "").replace(/\/+$/, "");
  if (!apiBase) return "";
  return `${apiBase}/v2/products/share/${encodeURIComponent(productId)}`;
};

const getProductPhotoUrl = (photos) => {
  const photo = Array.isArray(photos) ? photos[0] : photos;
  if (!photo) return "";
  const raw = String(photo).trim().replace(/\\/g, "/");
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  const filename = raw.includes("/") ? raw.split("/").filter(Boolean).pop() : raw;
  return filename ? `${config.UPLOADS_SERVER}/product/${filename}` : "";
};

const getProductTitle = (productDetails, fallback) =>
  pickDetail(productDetails, "productTitle") || fallback || "Product";

const getProductDescription = (productDetails) =>
  pickDetail(productDetails, "productDescription") || "";

function ProductShareModal({ open, product, onClose, t }) {
  const [copyFeedback, setCopyFeedback] = useState("");

  if (!open || !product) return null;

  const productTitle = getProductTitle(product.productDetails, t("product"));
  const productDescription = getProductDescription(product.productDetails);
  const imageUrl = getProductPhotoUrl(product.photos);
  const shareUrl = buildProductShareUrl(product._id);
  const shareText = [productTitle, productDescription].filter(Boolean).join("\n");
  const fullShareText = shareUrl ? `${shareText}\n\n${shareUrl}` : shareText;

  const copyValue = async (value, feedbackKey) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopyFeedback(feedbackKey);
      setTimeout(() => setCopyFeedback(""), 2200);
    } catch {
      Swal.fire(t("eventsError"), t("eventsShareCopyFailed"), "error");
    }
  };

  const openShareWindow = (url) => {
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer,width=640,height=520");
  };

  const handleNativeShare = async () => {
    if (!navigator.share) {
      Swal.fire(t("eventsShareUnavailable"), t("eventsShareUnavailableText"), "info");
      return;
    }
    try {
      const payload = {
        title: productTitle,
        text: shareText,
      };
      if (shareUrl) payload.url = shareUrl;
      await navigator.share(payload);
    } catch (error) {
      if (error?.name !== "AbortError") {
        Swal.fire(t("eventsError"), t("productShareFailed"), "error");
      }
    }
  };

  const shareOptions = [
    {
      key: "native",
      label: t("eventsShareNative"),
      icon: "fa-solid fa-share-nodes",
      className: "events-share-option--native",
      onClick: handleNativeShare,
      hidden: typeof navigator === "undefined" || !navigator.share,
    },
    {
      key: "facebook",
      label: "Facebook",
      icon: "fa-brands fa-facebook-f",
      className: "events-share-option--facebook",
      onClick: () => openShareWindow(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`),
      disabled: !shareUrl,
    },
    {
      key: "twitter",
      label: "X",
      icon: "fa-brands fa-x-twitter",
      className: "events-share-option--twitter",
      onClick: () =>
        openShareWindow(
          `https://twitter.com/intent/tweet?text=${encodeURIComponent(productTitle)}&url=${encodeURIComponent(shareUrl)}`
        ),
      disabled: !shareUrl,
    },
    {
      key: "whatsapp",
      label: "WhatsApp",
      icon: "fa-brands fa-whatsapp",
      className: "events-share-option--whatsapp",
      onClick: () => openShareWindow(`https://web.whatsapp.com/send?text=${encodeURIComponent(shareUrl || fullShareText)}`),
      disabled: !shareUrl && !fullShareText,
    },
    {
      key: "linkedin",
      label: "LinkedIn",
      icon: "fa-brands fa-linkedin-in",
      className: "events-share-option--linkedin",
      onClick: () =>
        openShareWindow(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`),
      disabled: !shareUrl,
    },
  ].filter((option) => !option.hidden);

  return (
    <div className="events-modal-backdrop events-modal-backdrop--studio" onClick={onClose} role="presentation">
      <div
        className="events-modal events-modal--studio events-modal--share"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="product-share-title"
      >
        <header className="events-studio-header">
          <div className="events-studio-header__icon events-studio-header__icon--share">
            <i className="fa-solid fa-share-nodes" />
          </div>
          <div className="events-studio-header__text">
            <h2 className="events-studio-header__title" id="product-share-title">
              {t("productShareTitle")}
            </h2>
            <p className="events-studio-header__subtitle">{t("productShareSubtitle")}</p>
          </div>
          <button type="button" className="events-modal__close" onClick={onClose} aria-label={t("eventsCancel")}>
            <i className="fa-solid fa-xmark" />
          </button>
        </header>

        <div className="events-share-body">
          <div className="events-share-preview">
            <div className="events-share-preview__media">
              {imageUrl ? (
                <img src={imageUrl} alt="" className="events-share-preview__image" />
              ) : (
                <img src={imgVide} alt="" className="events-share-preview__image events-share-preview__image--placeholder" />
              )}
            </div>
            <div className="events-share-preview__content">
              <h3 className="events-share-preview__title">{productTitle}</h3>
              {productDescription ? (
                <p className="events-share-preview__message">{productDescription}</p>
              ) : null}
              {shareUrl ? (
                <p className="events-share-preview__url">{shareUrl}</p>
              ) : (
                <p className="events-share-preview__url events-share-preview__url--missing">{t("productShareNoUrl")}</p>
              )}
              <p className="events-share-preview__hint">{t("productSharePreviewHint")}</p>
            </div>
          </div>

          <div className="events-share-options" role="list">
            {shareOptions.map((option) => (
              <button
                key={option.key}
                type="button"
                className={`events-share-option ${option.className}`}
                onClick={option.onClick}
                disabled={option.disabled}
                role="listitem"
              >
                <span className="events-share-option__icon" aria-hidden>
                  <i className={option.icon} />
                </span>
                <span>{option.label}</span>
              </button>
            ))}
          </div>

          <div className="events-share-copy">
            <button
              type="button"
              className="events-share-copy__btn"
              onClick={() => copyValue(shareUrl, "link")}
              disabled={!shareUrl}
            >
              <i className="fa-solid fa-link" aria-hidden />
              {copyFeedback === "link" ? t("eventsShareCopied") : t("eventsShareCopyLink")}
            </button>
            <button type="button" className="events-share-copy__btn" onClick={() => copyValue(fullShareText, "text")}>
              <i className="fa-solid fa-align-left" aria-hidden />
              {copyFeedback === "text" ? t("eventsShareCopied") : t("eventsShareCopyText")}
            </button>
            {imageUrl ? (
              <button type="button" className="events-share-copy__btn" onClick={() => copyValue(imageUrl, "image")}>
                <i className="fa-regular fa-image" aria-hidden />
                {copyFeedback === "image" ? t("eventsShareCopied") : t("eventsShareCopyImage")}
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProductShareModal;
